import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef, BsModalService, ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, interval, Subject, Subscription, timer } from 'rxjs';
import { catchError, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { AbstractFormComponent } from '../../../../../shared/crud/component/form.component';
import { ISelectOption } from '../../../../../shared/form/element/base-select/select-option.interface';
import {
    CameraProperty,
    CameraPropertySet,
    enumKeysCameraSettings,
    Property,
    SimpleCameraProperty,
} from './camera-property';
import { CustomerCameraSettingsForm } from './camera-settings.form';
import {
    CameraPropertiesNullArray,
    DefaultCameraSettingsNames,
    ForceDefaultSettings,
    NamesForSettings,
} from './default-camera-settings.model';
import { getOptions, getOptionsShorten } from './definitions';

@Component({
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
})
export class AdminCustomeCameraSettingsTableComponent extends AbstractFormComponent implements OnInit, OnDestroy {
    private currentSettingsSubscription: Subscription;
    private defaultShootingModeInForm: Subscription;
    options: { [key: string]: ISelectOption[] } = getOptions();
    optionsShorten: { [key: string]: ISelectOption[] } = getOptionsShorten();

    isForceDefaultSettings: ForceDefaultSettings = { isForced: false };
    isAllSettingsChange: boolean = false;
    isIntervalRefreshRunning: boolean = false;
    isPhotographerRecentlyActive: boolean = false;

    cameraPropertiesNullArray = JSON.parse(JSON.stringify(CameraPropertiesNullArray));
    defaultCameraSettingsNames = JSON.parse(JSON.stringify(DefaultCameraSettingsNames));
    defaultCameraProperties: SimpleCameraProperty[] = [];
    currentCameraProperties: SimpleCameraProperty[] = [];
    cameraModelAndModeDial: { modeDial: string; model: string } = { modeDial: '', model: '' };
    lastCurrentResponseRefreshTime: Date;
    currentLastUpdateDate: Date;

    modalRef: BsModalRef;

    @ViewChild('testPhotoCaptureModal', { static: false }) testPhotoCaptureModal: ModalDirective;

    private stopTestPhotoLoading$ = new Subject<void>();
    testPhotos: Array<{url: string}> = [];
    testPhotosAssumedCount = 0;
    currentCaptureId = '';
    isLoadingTestPhoto = false;


    constructor(
        formBuilder: CustomerCameraSettingsForm,
        httpClient: HttpClient,
        toastrService: ToastrService,
        router: Router,
        route: ActivatedRoute,
        private modalService: BsModalService,
    ) {
        super(formBuilder, httpClient, toastrService, router, route);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.getDefaultCameraProperties();
        this.getCurrentCameraProperties();
        this.getIsForceDefaultSettings();

        this.defaultShootingModeInForm = this.form.get('shootingMode').valueChanges.subscribe((value) => {
            this.onShootingModeChange(value);
        });
    }

    ngOnDestroy() {
        this.stopLoadingTestPhotos();
        this.stopSubscriptions();
    }

    protected getUrl(): string {
        const potographerUuid = this.route.snapshot.params.photographer;
        return `/admin/photographer-camera-properties/${potographerUuid}`;
    }

    protected onSubmit(): void {
        const properties: Property[] = this.reverseCameraProperties();

        this.httpClient.patch(this.getUrl() + '/default', { properties }).subscribe(
            (response: any) => {
                this.toastrService.success('Saved');
                this.setDefaultCameraProperties(response);
                this.refreshCurrentSettingsAfterSetDefaultSettings();
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return error;
            }
        );
    }

    protected onShootingModeChange(value: string | number) {
        const apertureValue = this.form.get('apertureValue');
        const shutterSpeed = this.form.get('shutterSpeed');

        if (value == 0x00) {
            apertureValue.disable();
            shutterSpeed.disable();
            this.defaultCameraSettingsNames.forEach((setting) => {
                if (setting.propertyName === 'apertureValue') {
                    setting.isEditMode = false;
                    setting.isAutoAdjust = true;
                }
                if (setting.propertyName === 'shutterSpeed') {
                    setting.isEditMode = false;
                    setting.isAutoAdjust = true;
                }
            });
        } else {
            apertureValue.enable();
            shutterSpeed.enable();
            this.defaultCameraSettingsNames.forEach((setting) => {
                if (setting.propertyName === 'apertureValue') {
                    setting.isAutoAdjust = false;
                }
                if (setting.propertyName === 'shutterSpeed') {
                    setting.isAutoAdjust = false;
                }
            });
        }
    }

    getDefaultCameraProperties() {
        this.httpClient.get(this.getUrl() + '/default').subscribe(
            (response: any) => {
                this.setDefaultCameraProperties(response);
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return error;
            }
        );
    }

    setDefaultCameraProperties(response: any) {
        this.defaultCameraProperties = this.createTemporaryCameraProperties(response.properties, 'default');

        // Create a map of the properties from the API response aka defaultCameraProperties
        const responsePropertiesMap = new Map(this.defaultCameraProperties.map((prop) => [prop.property, prop]));

        // Fill gaps with properties from CameraPropertiesNullArray
        this.cameraPropertiesNullArray.forEach((nullProp: SimpleCameraProperty) => {
            if (!responsePropertiesMap.has(nullProp.property)) {
                this.defaultCameraProperties.push(nullProp);
            }
        });

        this.setIsActiveAfterResponse(response.properties);

        const result = this.defaultCameraProperties.reduce((acc, item) => {
            if (Array.isArray(item.value)) {
                acc[item.property] = item.value.join('/');
            } else {
                acc[item.property] = item.value;
            }
            return acc;
        }, {});

        this.form.patchValue(result);
    }

    getCurrentCameraProperties() {
        this.httpClient.get(this.getUrl() + '/current').subscribe(
            (response: CameraPropertySet) => {
                this.toastrService.success('Current properties fetched');

                this.currentCameraProperties = this.createTemporaryCameraProperties(response.properties, 'current');
                this.isPhotographerRecentlyActive = response.isPhotographerRecentlyActive;

                const modeDial: SimpleCameraProperty = this.currentCameraProperties.find(
                    (item) => item.property === 'modeDial'
                ) || { property: 'modeDial', value: -1 };
                const modeDialItem: ISelectOption = this.options.modeDial.find((item) => item.value === modeDial.value);
                this.cameraModelAndModeDial = {
                    model: response.extras.model || '-',
                    modeDial: modeDialItem ? modeDialItem.label : '-',
                };

                this.lastCurrentResponseRefreshTime = new Date();
                this.currentLastUpdateDate = new Date(response.lastUpdateDate * 1000);
            },
            (error: any) => {
                this.toastrService.error(
                    error.error
                        ? error.error.message || 'The photographer must have used the camera within the last 3 hours '
                        : 'Error'
                );
                return error;
            }
        );
    }

    stopSubscriptions() {
        if (this.currentSettingsSubscription) {
            this.currentSettingsSubscription.unsubscribe();
        }
        if (this.defaultShootingModeInForm) {
            this.defaultShootingModeInForm.unsubscribe();
        }
    }

    getIsForceDefaultSettings() {
        this.httpClient.get(this.getUrl() + '/force').subscribe(
            (response: any) => {
                this.isForceDefaultSettings.isForced = response.isForced;
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return error;
            }
        );
    }

    setIsActiveAfterResponse(properties: Property[]): void {
        const propertyLookup = properties.reduce((acc, property) => {
            acc[enumKeysCameraSettings[property.property]] = property.isActive;
            return acc;
        }, {});

        this.defaultCameraSettingsNames = this.defaultCameraSettingsNames.map((item) =>
            item.propertyName in propertyLookup ? { ...item, isEditMode: propertyLookup[item.propertyName] } : item
        );
    }

    createTemporaryCameraProperties(
        properties: CameraPropertySet['properties'],
        nameType: string
    ): SimpleCameraProperty[] {
        return Object.values(properties).map((item: CameraProperty) => {
            let value: SimpleCameraProperty['value'];
            switch (item.dataType) {
                case 'ENUM_VALUE':
                    value = item.enumValue;
                    break;
                case 'VALUE':
                    value = item.value;
                    break;
                case 'ENUM_ARRAY':
                    value = item.enumArray;
                    break;
                default:
                    value = null;
            }

            const translatedProperty = enumKeysCameraSettings[item.property];
            const setting = this.defaultCameraSettingsNames.find(
                (setting: NamesForSettings) => setting.propertyName === translatedProperty
            );

            if (setting && setting.defaultSettingType === 'slider' && nameType === 'default') {
                const valueToBeClosest: number = value as number;
                const closest = setting.defaultShortenOptions.list.reduce((prev, curr) =>
                    Math.abs(curr.key - valueToBeClosest) < Math.abs(prev.key - valueToBeClosest) ? curr : prev
                );

                value = setting.defaultShortenOptions.list.findIndex((element) => element.key === closest.key);
            }

            if (setting && setting.defaultSettingType === 'slider' && nameType === 'current') {
                value = setting.currentFullOptions.list.findIndex(
                    (obj: { key: number; value: string }) => obj.key === value
                );
            }

            return {
                property: translatedProperty,
                value: value,
            };
        });
    }

    toggleAllSettings() {
        this.defaultCameraSettingsNames.forEach((setting: NamesForSettings) => {
            if (setting.isChangeable) {
                setting.isEditMode = this.isAllSettingsChange;
            }
        });
    }

    onForceDefaultSettings() {
        this.httpClient.patch(this.getUrl() + '/force', { isForced: !this.isForceDefaultSettings.isForced }).subscribe(
            (response: any) => {
                this.isForceDefaultSettings.isForced = response.isForced;
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return error;
            }
        );
    }

    getCurrentCameraPropertyValue(propertyName: string, defaultSettingType: string): any {
        const property = this.currentCameraProperties.find((item) => item.property === propertyName);
        if (!property) return undefined;
        if (defaultSettingType === 'dropdown' || defaultSettingType === 'disabled') {
            if (
                propertyName === 'stillPhotoImageQuality' &&
                Array.isArray(property.value) &&
                property.value.length === 3
            ) {
                const photoImage: number[] = [...property.value];
                return this.options[propertyName].find((option) => option.value === photoImage.join('/')).label;
            }
            const option = this.options[propertyName].find((option) => option.value === property.value);

            return option ? option.label : undefined;
        }

        return property.value;
    }

    getDefaultCameraFormValue(propertyName: string): any {
        const control = this.form.get(propertyName);

        return control ? (control.value ? control.value : 0) : 0;
    }

    updatePropertyValue(propertyName: string, newValue: any): void {
        const control = this.form.get(propertyName);
        control.setValue(newValue);
    }

    updateValue(name: string, event: any) {
        const index = (event.target as HTMLInputElement).value;
        this.form.get(name).patchValue(index);
    }

    refreshCurrentSettings() {
        this.getCurrentCameraProperties();
    }

    back() {
        this.router.navigate(['../../../users'], { relativeTo: this.route });
    }

    getCheckedCount(): number {
        return this.defaultCameraSettingsNames.filter((setting) => setting.isEditMode).length;
    }

    refreshCurrentSettingsAfterSetDefaultSettings() {
        if (!this.isIntervalRefreshRunning) {
            this.isIntervalRefreshRunning = true;
            const subscription: Subscription = interval(3000)
                .pipe(take(3))
                .subscribe({
                    next: () => {
                        this.getCurrentCameraProperties();
                    },
                    complete: () => {
                        this.isIntervalRefreshRunning = false;
                        subscription.unsubscribe();
                    },
                });
        }
    }

    reverseCameraProperties(): Property[] {
        if (!this.form.valid) {
            this.toastrService.error('Something is wrong... Try refreshing page.');
            return;
        }

        const nestedProperties: Property[] = [];
        let isShootingModeAsProgramAE: boolean = false;
        Object.keys(this.form.getRawValue()).forEach((key: string) => {
            const propertyKey: string = Object.keys(enumKeysCameraSettings).find(
                (_key) => enumKeysCameraSettings[_key] === key
            );

            if (propertyKey) {
                const setting: NamesForSettings = this.defaultCameraSettingsNames.find(
                    (value: NamesForSettings) => value.propertyName === key
                );

                const dataType: CameraProperty['dataType'] = setting.dataType;
                const formValue = this.form.get(key).value;

                const isActive = setting.isEditMode;
                let value: CameraProperty['value'] = null;
                let enumValue: CameraProperty['enumValue'] = null;
                let enumArray: CameraProperty['enumArray'] = null;

                if (setting && setting.defaultSettingType === 'slider') {
                    [value, enumValue, enumArray] = this.reverseSliders(
                        dataType,
                        formValue,
                        setting.defaultShortenOptions.list
                    );
                } else {
                    [value, enumValue, enumArray] = this.reverseCameraPropertiesSwitch(dataType, formValue);
                }

                if (propertyKey === 'SHOOTING_MODE' && enumValue === 0) {
                    isShootingModeAsProgramAE = true;
                }

                nestedProperties.push({
                    property: propertyKey,
                    dataType: dataType,
                    value: value,
                    enumValue: enumValue,
                    enumArray: enumArray,
                    isActive: isActive,
                });
            }
        });

        const propertiesToCheck = ['APERTURE_VALUE', 'SHUTTER_SPEED'];

        if (isShootingModeAsProgramAE) {
            nestedProperties.forEach((property) => {
                if (propertiesToCheck.includes(property.property)) {
                    property.isActive = false;
                }
            });
        }

        return nestedProperties;
    }

    reverseSliders(dataType: string, formValue: any, list?: any[]): [number, number, number[]] {
        let value: CameraProperty['value'] = null;
        let enumValue: CameraProperty['enumValue'] = null;
        let enumArray: CameraProperty['enumArray'] = null;

        let foundElement = list[formValue];

        switch (dataType) {
            case 'VALUE':
                value = foundElement.key;
                enumValue = null;
                enumArray = null;
                break;
            case 'ENUM_VALUE':
                value = null;
                enumValue = foundElement.key;
                enumArray = null;
                break;
            case 'ENUM_ARRAY':
                // not possible now
                break;
            default:
                value = null;
                enumValue = null;
                enumArray = null;
                break;
        }

        return [value, enumValue, enumArray];
    }

    reverseCameraPropertiesSwitch(dataType: string, formValue: any, list?: any[]): [number, number, number[]] {
        let value: CameraProperty['value'] = null;
        let enumValue: CameraProperty['enumValue'] = null;
        let enumArray: CameraProperty['enumArray'] = null;

        switch (dataType) {
            case 'VALUE':
                value = list
                    ? parseInt((list.find((item) => item.key === formValue) || { value: '0' }).value)
                    : formValue;
                enumValue = null;
                enumArray = null;
                break;
            case 'ENUM_VALUE':
                value = null;
                enumValue = parseInt(formValue) || 0;
                enumArray = null;
                break;
            case 'ENUM_ARRAY':
                value = null;
                enumValue = null;
                enumArray = formValue.split('/').map(Number);
                break;
            default:
                value = null;
                enumValue = null;
                enumArray = null;
                break;
        }

        return [value, enumValue, enumArray];
    }

    onConfirmCapturePhoto() {
        this.httpClient.patch(this.getUrl() + '/capture-photo', { }).subscribe(
            (response: any) => {
                this.toastrService.success('Event sent');
                this.showTestPhotoCaptureModal(response.captureId, response.sessionCount);
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message || 'Something went wrong...' : 'Error');
                return error;
            }
        );
    }

    showTestPhotoCaptureModal(captureId: string, count: number): void {
        this.testPhotoCaptureModal.show();

        this.testPhotosAssumedCount = count;
        this.testPhotos = [];
        this.currentCaptureId = captureId;
        this.isLoadingTestPhoto = true;

        timer(0, 10_000).pipe(
            takeUntil(this.stopTestPhotoLoading$),
            takeUntil(timer(300_000)),
            switchMap(() =>
                this.httpClient.get<any[]>(
                    `${this.getUrl()}/capture-photo/${this.currentCaptureId}`
                )
            ),
            tap((photos) => {
                this.testPhotos = photos;

                if (this.testPhotos.length >= this.testPhotosAssumedCount) {
                    this.toastrService.success('All test photos received');
                    this.stopLoadingTestPhotos();
                }
            }),
            catchError((error) => {
                this.toastrService.error(
                    error.error.message || 'Something went wrong...'
                );
                return EMPTY;
            })
        ).subscribe({
            complete: () => {
                this.isLoadingTestPhoto = false;
            }
        });
    }

    stopLoadingTestPhotos(): void {
        this.isLoadingTestPhoto = false;
        this.stopTestPhotoLoading$.next();
    }


    hideTestPhotoCaptureModal(): void {
        this.stopLoadingTestPhotos();

        this.testPhotoCaptureModal.hide();
        this.testPhotos = [];
        this.testPhotosAssumedCount = 0;
        this.currentCaptureId = '';
    }

}
